import React from 'react';
import clsx from 'clsx';
import { Headline } from '@/components/headline/Headline';
import SerializeDescription from '@/components/serialize-description/SerializeDescription';
import type { StepCardProps } from '@/components/card/types';
import { renameDomProps } from '@/utils/sanitizeDomProps';

const themeStyles: Record<
  NonNullable<StepCardProps['theme']>,
  { shell: string; number: string; title: string; description: string }
> = {
  light: {
    shell: 'bg-brand-100 border border-brand-200',
    number: 'text-brand-700',
    title: 'text-brand-800',
    description: 'text-neutral-500 [&_*]:text-neutral-500',
  },
  dark: {
    shell: 'bg-brand-700 border border-transparent',
    number: 'text-white',
    title: 'text-white',
    description: 'text-white/70 [&_*]:text-white/70',
  },
};

const StepCard: React.FC<StepCardProps> = ({
  // `variant` is consumed by the Card dispatcher; keep it off the DOM node.
  variant: _variant,
  index = 0,
  title,
  description,
  theme = 'light',
  className,
  ...rest
}) => {
  const styles = themeStyles[theme] ?? themeStyles.light;
  const stepNumber = String(index + 1).padStart(2, '0');
  const renamedProps = renameDomProps(rest);

  return (
    <div
      data-component-id="C034"
      className={clsx(
        'flex h-full min-h-[320px] flex-col rounded-2xl p-6 sm:min-h-[360px] sm:p-8',
        styles.shell,
        className,
      )}
      {...renamedProps}
    >
      <div className={clsx('text-3xl font-light leading-none', styles.number)}>
        {stepNumber}
      </div>
      {title && (
        <Headline
          level="h3"
          variant="headingFour"
          spacing="none"
          title={title}
          className={clsx('mt-5', styles.title)}
        />
      )}
      {description && (
        <div
          className={clsx(
            'mt-auto pt-8 text-base leading-relaxed',
            styles.description,
          )}
        >
          <SerializeDescription description={description} />
        </div>
      )}
    </div>
  );
};

export default StepCard;
